import { supabase } from "@/integrations/supabase/client";
import { log } from "@/utils/debug";
import type { PostgrestError, QueryData } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { TransformationFormState } from "./types";

const getClinicalCodeTransformationSelect = () =>
  supabase.from("clinical_code_transformations").select(
    `
            id,
            code_id,
            code_type,
            display_order,
            created_at,
            updated_at,
            condition_sets:clinical_code_transformation_condition_sets (
              id,
              transformation_id,
              created_at,
              updated_at,
              name,
              conditions:clinical_code_transformation_conditions (
                id,
                condition_set_id,
                field_id,
                datapoint_id,
                operator,
                created_at,
                updated_at,
                field:fields (
                  id,
                  name
                ),
                datapoint:datapoints (
                  id,
                  name,
                  smart_tags (
                    id,
                    tag_value
                  )
                )
              )
            )
          `,
  );

export type Transformation = QueryData<ReturnType<typeof getClinicalCodeTransformationSelect>>[0];

export const useClinicalCodeTransformations = () => {
  const queryClient = useQueryClient();

  // Fetch all transformations with their condition sets and conditions
  const fetchTransformationsQuery = useQuery({
    queryKey: ["clinical-code-transformations"],
    queryFn: async () => {
      try {
        // Fetch all transformations with nested condition sets, conditions, fields, datapoints, and smart_tags
        const { data: transformations, error: transformationsError } =
          await getClinicalCodeTransformationSelect().order("created_at", {
            ascending: false,
          });

        if (transformationsError) {
          log.error("[ClinicalCodeTransformations] Error fetching transformations:", transformationsError);
          throw transformationsError;
        }

        // Fetch code details for each transformation from icd and opcs tables
        const fullTransformations = await Promise.all(
          transformations.map(async (transformation) => {
            if (transformation.code_type === "ICD-10") {
              const { data, error } = await supabase
                .from("icd10_codes")
                .select("id, code, description")
                .eq("id", transformation.code_id)
                .maybeSingle();

              if (error) {
                log.error(
                  `[ClinicalCodeTransformations] Error fetching code details for ${transformation.code_id}:`,
                  error,
                );
              }

              return {
                ...transformation,
                code: data
                  ? {
                      id: data.id,
                      code: data.code,
                      description: data.description,
                    }
                  : null,
              };
            } else if (transformation.code_type === "OPCS-4") {
              const { data, error } = await supabase
                .from("opcs4_codes")
                .select("id, code, description")
                .eq("id", transformation.code_id)
                .maybeSingle();

              if (error) {
                log.error(
                  `[ClinicalCodeTransformations] Error fetching code details for ${transformation.code_id}:`,
                  error,
                );
              }

              return {
                ...transformation,
                code: data
                  ? {
                      id: data.id,
                      code: data.code,
                      description: data.description,
                    }
                  : null,
              };
            }

            return { ...transformation, code: null };
          }),
        );

        log.process("[ClinicalCodeTransformations] Successfully fetched transformations:", fullTransformations);
        return fullTransformations.sort((a, b) => (a.code?.code ?? "").localeCompare(b.code?.code ?? ""));
      } catch (error) {
        log.error("[ClinicalCodeTransformations] Error in fetchTransformationsQuery:", error);
        throw error;
      }
    },
    meta: {
      onError: (error: Error) => {
        toast.error(`Failed to fetch clinical code transformations: ${error.message}`);
      },
    },
  });

  // Create a new transformation
  const createTransformationMutation = useMutation({
    mutationFn: async (formData: TransformationFormState) => {
      log.process("[ClinicalCodeTransformations] Creating new transformation:", formData);

      try {
        // Step 1: Insert the transformation with code_id directly
        const { data: transformation, error: transformationError } = await supabase
          .from("clinical_code_transformations")
          .insert({
            code_id: formData.code_id,
            code_type: formData.code_type,
          })
          .select("id")
          .single();

        if (transformationError) throw transformationError;
        if (!transformation) throw new Error("No transformation data returned");

        // Step 2: Insert each condition set and its conditions
        for (const conditionSetData of formData.condition_sets) {
          // Insert condition set
          const { data: conditionSet, error: conditionSetError } = await supabase
            .from("clinical_code_transformation_condition_sets")
            .insert({
              transformation_id: transformation.id,
            })
            .select("id")
            .single();

          if (conditionSetError) throw conditionSetError;
          if (!conditionSet) throw new Error("No condition set data returned");

          // Insert conditions for this set one by one to avoid type issues
          if (conditionSetData.conditions.length > 0) {
            for (const condition of conditionSetData.conditions) {
              // Convert 'OR' operator to 'AND' since database doesn't accept 'OR'
              const safeOperator = condition.operator === "OR" ? "AND" : condition.operator;

              const { error: conditionError } = await supabase.from("clinical_code_transformation_conditions").insert({
                condition_set_id: conditionSet.id,
                field_id: condition.field_id,
                datapoint_id: condition.datapoint_id,
                operator: safeOperator,
              });

              if (conditionError) throw conditionError;
            }
          }
        }

        return transformation;
      } catch (error) {
        log.error("[ClinicalCodeTransformations] Error creating transformation:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clinical-code-transformations"],
      });
      toast.success("Transformation created successfully");
    },
    onError: (error: PostgrestError | Error) => {
      log.error("[ClinicalCodeTransformations] Error in create mutation:", error);
      toast.error(`Failed to create transformation: ${(error as Error).message}`);
    },
  });

  // Update an existing transformation
  const updateTransformationMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: TransformationFormState }) => {
      log.process(`[ClinicalCodeTransformations] Updating transformation ${id}:`, formData);

      try {
        // Step 1: Update the transformation with code_id directly
        const { error: transformationError } = await supabase
          .from("clinical_code_transformations")
          .update({
            code_id: formData.code_id,
            code_type: formData.code_type,
          })
          .eq("id", id);

        if (transformationError) throw transformationError;

        // Step 2: Delete all existing condition sets (cascade will delete conditions)
        const { error: deleteConditionSetsError } = await supabase
          .from("clinical_code_transformation_condition_sets")
          .delete()
          .eq("transformation_id", id);

        if (deleteConditionSetsError) throw deleteConditionSetsError;

        // Step 3: Insert each condition set and its conditions
        for (const conditionSetData of formData.condition_sets) {
          // Insert condition set
          const { data: conditionSet, error: conditionSetError } = await supabase
            .from("clinical_code_transformation_condition_sets")
            .insert({
              transformation_id: id,
            })
            .select("id")
            .single();

          if (conditionSetError) throw conditionSetError;
          if (!conditionSet) throw new Error("No condition set data returned");

          // Insert conditions for this set one by one to avoid type issues
          if (conditionSetData.conditions.length > 0) {
            for (const condition of conditionSetData.conditions) {
              // Convert 'OR' operator to 'AND' since database doesn't accept 'OR'
              const safeOperator = condition.operator === "OR" ? "AND" : condition.operator;

              const { error: conditionError } = await supabase.from("clinical_code_transformation_conditions").insert({
                condition_set_id: conditionSet.id,
                field_id: condition.field_id,
                datapoint_id: condition.datapoint_id,
                operator: safeOperator,
              });

              if (conditionError) throw conditionError;
            }
          }
        }

        return { id };
      } catch (error) {
        log.error("[ClinicalCodeTransformations] Error updating transformation:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clinical-code-transformations"],
      });
      toast.success("Transformation updated successfully");
    },
    onError: (error: PostgrestError | Error) => {
      log.error("[ClinicalCodeTransformations] Error in update mutation:", error);
      toast.error(`Failed to update transformation: ${(error as Error).message}`);
    },
  });

  // Delete a transformation
  const deleteTransformationMutation = useMutation({
    mutationFn: async (id: string) => {
      log.process(`[ClinicalCodeTransformations] Deleting transformation ${id}`);

      const { error } = await supabase.from("clinical_code_transformations").delete().eq("id", id);

      if (error) {
        log.error("[ClinicalCodeTransformations] Error deleting transformation:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["clinical-code-transformations"],
      });
      toast.success("Transformation deleted successfully");
    },
    onError: (error: PostgrestError | Error) => {
      log.error("[ClinicalCodeTransformations] Error in delete mutation:", error);
      toast.error(`Failed to delete transformation: ${(error as Error).message}`);
    },
  });

  // Get transformation by ID
  const getTransformationById = (id: string): Transformation | undefined => {
    if (fetchTransformationsQuery.data) {
      return fetchTransformationsQuery.data.find((t) => t.id === id);
    }
    return undefined;
  };

  return {
    transformations: fetchTransformationsQuery.data || [],
    isLoading: fetchTransformationsQuery.isLoading,
    error: fetchTransformationsQuery.error,
    createTransformation: createTransformationMutation,
    updateTransformation: updateTransformationMutation,
    deleteTransformation: deleteTransformationMutation,
    getTransformationById,
    refetch: fetchTransformationsQuery.refetch,
  };
};
